// Control Center › Briefversand (E-POSTBUSINESS API): Ein/Aus, Absender, Test-Adresse, Verbindung prüfen.
// Gespeichert wird beim Verlassen eines Feldes bzw. beim Umschalten (app_settings 'epost_versand').
import { useEffect, useState } from 'react';
import { CheckCircle, Info, PlugsConnected, WarningCircle } from '@phosphor-icons/react';
import { SchalterZeile } from '../../../components/immobilien/ui';
import { Segment } from '../../../components/ui/Segment';
import { ladeEpostEinstellung, pruefeEpostVerbindung, speichereEpostEinstellung, type EpostEinstellung, type Verbindung } from '../../../lib/epost/epost';
import { CcWidget, KopfIcon } from './Rahmen';
import { PostLogo } from '../../../components/epost/PostLogo';

const feldTitel = { fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' } as const;
const feld = { height: 44, padding: '0 14px', borderRadius: 14, border: 'none', outline: 'none', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 14, color: 'var(--ink)', minWidth: 0 } as const;

export function EpostWidget({ spalte }: { spalte: string }) {
  const [e, setE] = useState<EpostEinstellung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);
  const [pruefung, setPruefung] = useState<Verbindung | 'laeuft' | null>(null);

  useEffect(() => { void ladeEpostEinstellung().then(setE, (err: Error) => setFehler(err.message)); }, []);

  const speichern = (neu: EpostEinstellung) => {
    setFehler(null); setE(neu);
    void speichereEpostEinstellung(neu).then(() => { setGespeichert(true); setTimeout(() => setGespeichert(false), 1800); }, (err: Error) => setFehler(err.message));
  };
  const absenderFeld = (k: keyof EpostEinstellung['absender'], platzhalter: string, flex = 1) => (
    <input aria-label={platzhalter} placeholder={platzhalter} value={e?.absender[k] ?? ''} disabled={!e} style={{ ...feld, flex }}
      onChange={(ev) => e && setE({ ...e, absender: { ...e.absender, [k]: ev.target.value } })}
      onBlur={() => e && speichern(e)} />
  );
  const liveBestaetigt = () => window.prompt('Ab jetzt druckt die Post jeden Brief und berechnet Porto. Zum Bestätigen LIVE eintippen:')?.trim().toUpperCase() === 'LIVE';
  const pruefen = () => {
    setPruefung('laeuft');
    void pruefeEpostVerbindung().then(setPruefung, (err: Error) => setPruefung({ ok: false, erreichbar: false, anmeldung: false, fehler: err.message, live: false, aktiv: false }));
  };

  return (
    <CcWidget titel="Briefversand" untertitel="Briefe per E-POST verschicken" spalte={spalte}
      icon={<KopfIcon flaeche="#fff" farbe="#000"><PostLogo size={22} /></KopfIcon>}
      rechts={gespeichert ? <span role="status" style={{ fontSize: 12, fontWeight: 800, color: 'var(--ok)' }}>gespeichert</span> : undefined}>
      <SchalterZeile an={e?.aktiv === true} disabled={!e} label="Briefe per Post versenden" onChange={(an) => e && speichern({ ...e, aktiv: an })} />
      <div className="flex flex-col" style={{ gap: 8 }}>
        <span style={feldTitel}>Versandart</span>
        <Segment<'test' | 'live'> ariaLabel="Test oder Live" wert={e?.modus ?? 'test'} hoehe={44} voll
          onChange={(m) => { if (!e || m === e.modus) return; if (m === 'live' && !liveBestaetigt()) return; speichern({ ...e, modus: m }); }}
          eintraege={[{ key: 'test', label: 'Test' }, { key: 'live', label: 'Live' }]} />
        <span role="status" style={{ fontSize: 12.5, fontWeight: 700, padding: '8px 12px', borderRadius: 12,
          background: e?.modus === 'live' ? 'var(--bad-soft)' : 'var(--ok-soft)', color: e?.modus === 'live' ? 'var(--bad)' : 'var(--ok)' }}>
          {e?.modus === 'live'
            ? 'LIVE: Die Post druckt jeden Brief, stellt ihn zu und berechnet Porto.'
            : 'Test: Nichts wird gedruckt, kein Porto. Die Post schickt nur eine Vorschau an die Test-Adresse.'}
        </span>
        <span style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.5 }}>
          Live funktioniert erst, wenn der Post-Support das Konto auf LIVE umgestellt hat – vorher lehnt die Post echte Briefe ab (Meldung im Dialog). Umschalten dürfen nur Admins.
        </span>
      </div>
      <p className="m-0 flex" style={{ gap: 9, padding: '12px 14px', borderRadius: 16, background: 'var(--fill)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink2)' }}>
        <Info size={16} weight="fill" color="var(--ink3)" className="shrink-0" style={{ marginTop: 1 }} />
        <span>Ein PDF aus der App geht an die Deutsche Post. Dort wird es gedruckt, kuvertiert, frankiert und zugestellt. Den Stand jedes Briefs zeigt die App an: angenommen, im Druckzentrum, versendet oder Fehler. Solange der Testmodus läuft, wird nichts gedruckt. Stattdessen kommt eine Vorschau als PDF an die Test-Adresse. Ausgeschaltet nimmt die App keine Briefe an.</span>
      </p>
      <SchalterZeile an={e?.status_abgleich !== false} disabled={!e || !e.aktiv} label="Stand stündlich bei der Post abfragen"
        zusatz="Aktualisiert jede Stunde den Stand offener Briefe (angenommen, im Druckzentrum, versendet, Fehler) und Einschreiben-Nummern. Aus: nur beim Öffnen des Verlaufs." onChange={(an) => e && speichern({ ...e, status_abgleich: an })} />
      <div className="flex flex-col" style={{ gap: 8 }}>
        <span style={feldTitel}>Absender (Zeile über der Anschrift)</span>
        {absenderFeld('name', 'Firma / Name')}
        {absenderFeld('strasse', 'Straße und Hausnummer')}
        <div className="flex" style={{ gap: 8 }}>{absenderFeld('plz', 'PLZ', 0.4)}{absenderFeld('ort', 'Ort')}</div>
      </div>
      <label className="flex flex-col" style={{ gap: 8 }}>
        <span style={feldTitel}>Test-Adresse für Vorschau-PDFs (nur im Testmodus)</span>
        <input type="email" placeholder="name@firma.de" value={e?.test_email ?? ''} disabled={!e} style={feld}
          onChange={(ev) => e && setE({ ...e, test_email: ev.target.value })} onBlur={() => e && speichern(e)} />
      </label>
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <button type="button" onClick={pruefen} disabled={pruefung === 'laeuft'} className="inline-flex items-center"
          style={{ gap: 7, height: 40, padding: '0 16px', borderRadius: 20, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          <PlugsConnected size={16} weight="bold" />{pruefung === 'laeuft' ? 'Prüfe …' : 'Verbindung prüfen'}
        </button>
        {pruefung && pruefung !== 'laeuft' && (
          <span className="inline-flex items-center" style={{ gap: 6, fontSize: 12.5, fontWeight: 700, color: pruefung.ok ? 'var(--ok)' : 'var(--bad)' }}>
            {pruefung.ok ? <CheckCircle size={15} weight="fill" /> : <WarningCircle size={15} weight="fill" />}
            {pruefung.ok ? `Verbunden · ${pruefung.live ? 'echter Versand' : 'Testmodus'}` : (pruefung.fehler ?? 'Post nicht erreichbar')}
          </span>
        )}
      </div>
      {fehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>Nicht gespeichert: {fehler}</span>}
    </CcWidget>
  );
}
